<template>
  <div>
    <div v-if="!isReady" class="fixed inset-0 bg-white z-[99999]"></div>

    <div v-else class="min-h-screen bg-white ticket-landing">
      <!-- ===== Boxed Container for Header + Slider ===== -->
      <div class="hero-wrapper">
        <!-- ===== Top Bar with Logo ===== -->
        <header class="hero-top-bar">
          <div class="hero-top-bar-content">
            <a
              href="https://www.summerjam.com.au"
              target="_blank"
              class="inline-flex items-center"
            >
              <img
                src="/src/assets/images/logos/sj-logo-2025.svg"
                alt="Summer Jam Logo"
                class="h-8 sm:h-10 w-auto"
              />
            </a>
          </div>
        </header>

        <!-- ===== Hero Image Slider ===== -->
        <section class="hero-slider-section">
          <div 
            class="hero-slider" 
            ref="heroSliderRef"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <!-- Slides -->
            <div 
              v-for="(slide, index) in heroSlides" 
              :key="index"
              class="hero-slide"
              :class="{ 'active': currentSlide === index }"
            >
              <img
                :src="slide.url"
                :alt="slide.alt || event?.name || 'Event image'"
                class="hero-slide-media"
                referrerpolicy="no-referrer"
              />
            </div>

            <!-- Navigation Arrows -->
            <button 
              v-if="heroSlides.length > 1"
              @click="prevSlide"
              class="hero-slider-arrow hero-slider-arrow--prev"
              aria-label="Previous slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button 
              v-if="heroSlides.length > 1"
              @click="nextSlide"
              class="hero-slider-arrow hero-slider-arrow--next"
              aria-label="Next slide"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <!-- Dots -->
            <div v-if="heroSlides.length > 1" class="hero-slider-dots">
              <button
                v-for="(_, index) in heroSlides"
                :key="index"
                @click="goToSlide(index)"
                class="hero-slider-dot"
                :class="{ 'active': currentSlide === index }"
                :aria-label="`Go to slide ${index + 1}`"
              />
            </div>
          </div>
        </section>
      </div>

      <!-- ===== Main ===== -->
      <main class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Title row -->
        <div class="py-6 lg:py-10">
          <div class="flex items-start justify-between gap-6">
            <h1 class="text-2xl sm:text-3xl lg:text-[32px] font-semibold text-slate-900 leading-tight">
              {{ event?.landing_page_heading || 'Event' }}
            </h1>
          </div>

          <!-- Meta -->
          <div class="gap-6 mt-4 flex flex-wrap items-center gap-y-4 text-[15px]">
            <div>
              <div class="flex  gap-2 text-slate-700">
                <div class="pr-2">
                  <img class="ticket-landing__header-icon" src="/src/assets/images/icons/icon-calendar-round-black.png" />
                </div>
                <!-- <span class="inline-flex w-8 h-8 items-center justify-center rounded-full bg-slate-100">🗓</span> -->
                <div class="relative top-1.5">
                  <div>
                      <span>{{ event?.display?.schedule || '' }}</span>
                  </div>
                  <div>
                    <!-- Calendar links -->
                    <a :href="icsUrl" class="text-xs mr-1 text-[#fa552d] hover:underline" download>+Cal</a>
                    <a :href="googleUrl" target="_blank" rel="noopener" class="text-xs mr-1 text-[#fa552d] hover:underline">+Google</a>
                    <a :href="outlookUrl" target="_blank" rel="noopener" class="text-xs text-[#fa552d] hover:underline">+Outlook</a>
                  </div>                  
                </div>
              </div>
            </div>

            <div class="md:pl-6 md:ml-6 md:border-l-black md:border-l flex gap-2 text-slate-700" v-if="locationText">
              <div class="pr-2">
                <img class="ticket-landing__header-icon" src="/src/assets/images/icons/icon-map-round-black.png" />
              </div>
              <div class="relative top-1.5">
                <div>
                  <span>{{ locationText }}</span>
                </div>
                <div>
                  <a :href="mapUrl" target="_blank" rel="noopener" class="text-xs text-[#fa552d] hover:underline">View on Map</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ===== Grid ===== -->
        <div class="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-3 gap-8">
          <!-- ===== Left: Content ===== -->
          <div class="md:col-span-3 lg:col-span-2">
            <!-- Description -->
            <section class="border-t border-slate-200 pt-6">
              <h2 class="text-xl font-semibold text-slate-900 mb-5">Event Description</h2>

              <!-- WYSIWYG content -->
              <div
                class="wysiwyg"
                v-html="event?.description"
              />
            </section>

            <!-- Per-day / group information blocks -->
            <section
              v-if="groupsWithInfo.length"
              class="mt-8 space-y-4 sm:space-y-6"
            >
              <div
                v-for="g in groupsWithInfo"
                :key="g.id"
                class="group-info-card rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-1 sm:px-5 sm:py-2"
              >
                <div
                  class="wysiwyg text-sm"
                  v-html="g.additional_event_information"
                />
              </div>
            </section>            

            <!-- ===== FAQs (from API) ===== -->
            <section class="mt-10 border-t border-slate-200 pt-6" v-if="faqs.length">
              <h2 class="text-xl font-semibold text-slate-900 mb-4">FAQS</h2>

              <div class="rounded-lg border border-slate-200 overflow-hidden">
                <div
                  v-for="(f, idx) in faqs"
                  :key="f.id ?? idx"
                  class="faq-item border-b border-slate-200 last:border-0"
                >
                  <!-- Button -->
                  <button
                    class="faq-btn w-full text-left px-5 py-4 flex items-center justify-between"
                    @click="toggleFaq(f.id ?? idx)"
                    :aria-expanded="!!openFaq[f.id ?? idx]"
                  >
                    <span class="font-medium text-slate-900 pr-6">{{ f.question }}</span>
                    <span class="text-slate-500 tabular-nums">{{ openFaq[f.id ?? idx] ? '–' : '+' }}</span>
                  </button>

                  <!-- Animated panel -->
                  <transition
                    name="faq-slide"
                    @before-enter="faqBeforeEnter"
                    @enter="faqEnter"
                    @before-leave="faqBeforeLeave"
                    @leave="faqLeave"
                  >
                    <div
                      v-show="openFaq[f.id ?? idx]"
                      class="faq-panel px-5  text-slate-700"
                    >
                    <div class="pb-4">
                      {{ f.answer }}
                    </div>
                      
                    </div>
                  </transition>
                </div>
              </div>
            </section>

            <!-- Location -->
            <section class="mt-10 border-t border-slate-200 pt-6" v-if="locationText">
              <h2 class="text-xl font-semibold text-slate-900 mb-4">Location</h2>

            <div class="flex gap-2 text-slate-700 mb-7" v-if="locationText">
              <div class="pr-2">
                <img class="ticket-landing__header-icon" src="/src/assets/images/icons/icon-map-round-black.png" />
              </div>
              <div class="relative top-1.5">
                <div>
                  <span>{{ locationText }}</span>
                </div>
                <div>
                  <a :href="mapUrl" target="_blank" rel="noopener" class="text-xs text-[#fa552d] hover:underline">View on Map</a>
                </div>
              </div>
            </div>            

              <div class="mt-4 rounded-lg overflow-hidden border border-slate-200">
                <iframe
                  title="Map"
                  class="w-full h-[300px]"
                  style="border:0"
                  loading="lazy"
                  allowfullscreen
                  referrerpolicy="no-referrer-when-downgrade"
                  :src="embedMapUrl"
                />
              </div>
            </section>



            <section class="mt-10 border-t border-slate-200 pt-6">
              <h2 class="text-xl font-semibold text-slate-900 mb-5">Conditions Of Entry</h2>

              <!-- WYSIWYG content -->
              <div
                class="wysiwyg conditions_of_entry"
                v-html="event?.conditions_of_entry"
              />
            </section>      
            
            <!-- Contact -->
            <section class="my-10 border-t border-slate-200 pt-6 ">
              <h2 class="text-[18px] font-semibold text-slate-900 mb-4">Have More Questions About the Event?</h2>
              <p class="text-slate-700">
                Contact the Organiser
                <a href="mailto:info@summerjam.com.au" class="font-bold black underline">via email</a>
              </p>
            </section>            
          </div>

          <!-- ===== Right: Sidebar ===== -->
          <aside class="md:col-span-2 lg:col-span-1 hidden md:block">
            <div class="lg:sticky lg:top-6">
              <div class="rounded-xl border border-slate-200 overflow-hidden">
                <div class="p-4 border-b border-slate-200">
                  <h3 class="font-semibold text-slate-900">{{ event?.name || 'Event' }}</h3>
                </div>
                <div class="p-4 space-y-3 text-[15px]">
                  <div class="flex gap-3">
                    <span class="inline-flex w-6">

                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.75002 1C4.75002 0.801088 4.671 0.610322 4.53035 0.46967C4.3897 0.329018 4.19893 0.25 4.00002 0.25C3.80111 0.25 3.61034 0.329018 3.46969 0.46967C3.32904 0.610322 3.25002 0.801088 3.25002 1V2.816C2.52048 2.91945 1.84483 3.25882 1.32625 3.78228C0.807669 4.30573 0.474642 4.98452 0.378019 5.715L0.291019 6.368C0.276352 6.478 0.262353 6.58867 0.249019 6.7C0.240954 6.76928 0.247681 6.83948 0.268756 6.90597C0.28983 6.97245 0.324772 7.03371 0.371273 7.0857C0.417775 7.13768 0.474777 7.1792 0.538514 7.20753C0.602251 7.23585 0.671273 7.25033 0.741019 7.25H17.26C17.3298 7.25033 17.3988 7.23585 17.4625 7.20753C17.5263 7.1792 17.5833 7.13768 17.6298 7.0857C17.6763 7.03371 17.7112 6.97245 17.7323 6.90597C17.7534 6.83948 17.7601 6.76928 17.752 6.7L17.71 6.368L17.623 5.715C17.5264 4.98452 17.1934 4.30573 16.6748 3.78228C16.1562 3.25882 15.4806 2.91945 14.751 2.816V1C14.751 0.801088 14.672 0.610322 14.5313 0.46967C14.3907 0.329018 14.1999 0.25 14.001 0.25C13.8021 0.25 13.6113 0.329018 13.4707 0.46967C13.33 0.610322 13.251 0.801088 13.251 1V2.668C10.4233 2.41664 7.57878 2.41664 4.75102 2.668L4.75002 1ZM17.945 9.226C17.9403 9.09778 17.886 8.97641 17.7934 8.88757C17.7008 8.79873 17.5773 8.7494 17.449 8.75H0.551019C0.422717 8.7494 0.299212 8.79873 0.20664 8.88757C0.114069 8.97641 0.0596928 9.09778 0.0550192 9.226C-0.00411167 11.0357 0.106371 12.8469 0.385019 14.636C0.487719 15.2951 0.80647 15.9015 1.2912 16.3598C1.77593 16.8181 2.39917 17.1024 3.06302 17.168L4.25602 17.286C7.41102 17.596 10.589 17.596 13.744 17.286L14.937 17.168C15.6009 17.1024 16.2241 16.8181 16.7088 16.3598C17.1936 15.9015 17.5123 15.2951 17.615 14.636C17.8937 12.8469 18.0042 11.0357 17.945 9.226Z" fill="black"/>
                      </svg>                    
                    </span>
                    <div>
                      <div>{{ event?.display?.date_range || (start ? start.format('ddd, D MMM') : '') }}</div>
                      <div class="text-slate-500 text-sm">{{ timeOnly }}</div>
                    </div>
                  </div>
                  <div class="flex gap-3" v-if="locationText">
                    <span class="inline-flex w-6">
                      <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50744 19.4551C7.50744 19.4551 0.731934 13.7488 0.731934 8.13398C0.731934 6.15439 1.51832 4.25588 2.91811 2.8561C4.31789 1.45631 6.2164 0.669922 8.196 0.669922C10.1756 0.669922 12.0741 1.45631 13.4739 2.8561C14.8737 4.25588 15.6601 6.15439 15.6601 8.13398C15.6601 13.7488 8.88456 19.4551 8.88456 19.4551C8.50762 19.8022 7.88717 19.7984 7.50744 19.4551ZM8.196 11.3995C8.62483 11.3995 9.04947 11.315 9.44566 11.1509C9.84185 10.9868 10.2018 10.7463 10.5051 10.4431C10.8083 10.1398 11.0488 9.77984 11.2129 9.38365C11.3771 8.98746 11.4615 8.56282 11.4615 8.13398C11.4615 7.70515 11.3771 7.28051 11.2129 6.88432C11.0488 6.48813 10.8083 6.12814 10.5051 5.82491C10.2018 5.52168 9.84185 5.28114 9.44566 5.11703C9.04947 4.95292 8.62483 4.86846 8.196 4.86846C7.32992 4.86846 6.49932 5.2125 5.88692 5.82491C5.27451 6.43731 4.93047 7.26791 4.93047 8.13398C4.93047 9.00006 5.27451 9.83066 5.88692 10.4431C6.49932 11.0555 7.32992 11.3995 8.196 11.3995Z" fill="black"/>
                      </svg>                    
                    </span>
                    <div>
                      <div
                        v-for="(line, i) in locationLines"
                        :key="i"
                        :class="i === 1 ? 'text-slate-500 text-sm' : ''"
                      >
                        {{ line }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="p-4 border-t border-slate-200">
                  <button
                    @click="openTicketsWidget"
                    class="btn-ticketing-red"
                  >
                    Get Tickets
                  </button>
                </div>

              </div>
              <div class="mt-3">
                <a
                  class="cursor-pointer text-xs text-[#fa552d] underline"
                  @click="openLostWidget"
                >
                  Lost Your Tickets?
                </a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <!-- ===== Footer ===== -->
      <footer class="mt-10 ticket-landing__footer">
        <div class="h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between text-white font-bold">
          <img class="ticket-landing__footer__logo" src="/src/assets/images/logos/sj-logo-2025.svg" />
          <div class="flex flex-col justify-between h-[80%] items-end">
            <a href="https://www.summerjam.com.au">summerjam.com.au</a>
            <div class="flex items-center gap-4 ticket-landing__footer__socials">
              <a class='facebook' href="https://www.facebook.com/summerjamaustralia/" target="_blank">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 10C20 4.48 15.52 0 10 0C4.48 0 0 4.48 0 10C0 14.84 3.44 18.87 8 19.8V13H6V10H8V7.5C8 5.57 9.57 4 11.5 4H14V7H12C11.45 7 11 7.45 11 8V10H14V13H11V19.95C16.05 19.45 20 15.19 20 10Z" fill="white"/>
                </svg>
              </a>
              <a class='instagram' href="https://www.instagram.com/summerjamau/" target="_blank">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.77 1.08202C8.24603 0.696945 11.754 0.696945 15.23 1.08202C17.129 1.29402 18.66 2.78902 18.883 4.69502C19.2949 8.21969 19.2949 11.7803 18.883 15.305C18.66 17.211 17.129 18.706 15.231 18.919C11.7546 19.3042 8.24636 19.3042 4.77 18.919C2.871 18.706 1.34 17.211 1.117 15.306C0.704979 11.781 0.704979 8.22002 1.117 4.69502C1.34 2.78902 2.871 1.29402 4.77 1.08202ZM15 4.00002C14.7348 4.00002 14.4804 4.10537 14.2929 4.29291C14.1054 4.48045 14 4.7348 14 5.00002C14 5.26523 14.1054 5.51959 14.2929 5.70712C14.4804 5.89466 14.7348 6.00002 15 6.00002C15.2652 6.00002 15.5196 5.89466 15.7071 5.70712C15.8946 5.51959 16 5.26523 16 5.00002C16 4.7348 15.8946 4.48045 15.7071 4.29291C15.5196 4.10537 15.2652 4.00002 15 4.00002ZM5.25 10C5.25 8.74024 5.75044 7.53206 6.64124 6.64126C7.53204 5.75046 8.74022 5.25002 10 5.25002C11.2598 5.25002 12.468 5.75046 13.3588 6.64126C14.2496 7.53206 14.75 8.74024 14.75 10C14.75 11.2598 14.2496 12.468 13.3588 13.3588C12.468 14.2496 11.2598 14.75 10 14.75C8.74022 14.75 7.53204 14.2496 6.64124 13.3588C5.75044 12.468 5.25 11.2598 5.25 10Z" fill="white"/>
                </svg>
              </a>
              <a class='youtube' href="https://www.youtube.com/@summerjamau" target="_blank">
                <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0C10.855 0 11.732 0.0220001 12.582 0.0580001L13.586 0.106L14.547 0.163L15.447 0.224L16.269 0.288C17.1612 0.355891 18.0008 0.736434 18.64 1.3626C19.2791 1.98877 19.6768 2.8204 19.763 3.711L19.803 4.136L19.878 5.046C19.948 5.989 20 7.017 20 8C20 8.983 19.948 10.011 19.878 10.954L19.803 11.864L19.763 12.289C19.6768 13.1798 19.2789 14.0115 18.6396 14.6377C18.0002 15.2639 17.1604 15.6443 16.268 15.712L15.448 15.775L14.548 15.837L13.586 15.894L12.582 15.942C11.7218 15.9793 10.861 15.9986 10 16C9.13902 15.9986 8.27817 15.9793 7.418 15.942L6.414 15.894L5.453 15.837L4.553 15.775L3.731 15.712C2.83881 15.6441 1.9992 15.2636 1.36004 14.6374C0.720888 14.0112 0.323191 13.1796 0.237 12.289L0.197 11.864L0.122 10.954C0.0458327 9.97112 0.00513964 8.98581 0 8C0 7.017 0.052 5.989 0.122 5.046L0.197 4.136L0.237 3.711C0.323157 2.82055 0.720712 1.98905 1.35966 1.36291C1.99861 0.736759 2.83799 0.356116 3.73 0.288L4.551 0.224L5.451 0.163L6.413 0.106L7.417 0.0580001C8.2775 0.0207272 9.13869 0.00138958 10 0ZM8 5.575V10.425C8 10.887 8.5 11.175 8.9 10.945L13.1 8.52C13.1914 8.46739 13.2673 8.39162 13.3201 8.30033C13.3729 8.20904 13.4007 8.10545 13.4007 8C13.4007 7.89455 13.3729 7.79096 13.3201 7.69967C13.2673 7.60838 13.1914 7.53261 13.1 7.48L8.9 5.056C8.80876 5.00332 8.70526 4.9756 8.5999 4.97562C8.49455 4.97563 8.39106 5.00339 8.29983 5.0561C8.20861 5.1088 8.13287 5.1846 8.08024 5.27587C8.02761 5.36713 7.99993 5.47065 8 5.576V5.575Z" fill="white"/>
                </svg>
              </a>            
            </div>
          </div>
        </div>
      </footer>

      <!-- ===== Mobile Sticky CTA ===== -->
      <div class="bg-white ticket-landing__mobile-cta md:hidden fixed inset-x-0 bottom-0  z-40 pb-[env(safe-area-inset-bottom)]">

        <div class="ticket-landing__mobile-cta--lost-tickets">
              <div class="">
                <a 
                  class="cursor-pointer ticket-landing__mobile--lost-tickets--btn"
                  @click="openLostWidget"
                >
                    Lost Your Tickets?
                </a>
              </div>
        </div>
        <div class="ticket-landing__mobile-cta--btn">
          <button
              @click="openTicketsWidget"
            class="btn-ticketing-red "
          >
            Get Tickets
          </button>
        </div>

      </div>

      <!-- ===== Modal ===== -->
      <Teleport to="body">
        <div
          v-if="openWidget"
          class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 lg:p-4"
        >
          <div
            :class="modalClasses"
          >
            <button
              @click="closeWidget"
              class="close-button absolute top-3 right-3 z-10 text-white bg-black/60 hover:bg-black/80 rounded-full w-8 h-8 flex items-center justify-center"
            >
              ✕
            </button>
            <div
              ref="modalScrollRef"
              class="modal-scroll h-full overflow-auto lg:overflow-y-auto"
            >
              <!-- Ticket purchase widget -->
              <TicketWidget
                v-if="activeWidget === 'tickets'"
                :slug="slug"
              />

              <!-- Lost tickets widget -->
              <LostTicketsWidget
                v-else
              />
            </div>
          </div>
        </div>
      </Teleport>

<!-- Close ticket widget confirmation modal -->
      <Dialog
        size="lg"
        :open="showCloseConfirmModal"
        @close="() => { showCloseConfirmModal = false }"
        class="z-[99999999]"
      >
        <Dialog.Panel class="p-8 text-center">
          <h2 class="text-lg font-semibold mb-3">
            Close ticket window?
          </h2>
          <p class="text-sm text-slate-600 mb-6">
            Heads up! Closing this window will reset your ticket choices - you’ll need to start again.
          </p>

          <div class="mt-2 flex justify-center gap-4">
            <Button
              variant="outline-secondary"
              class="px-5 h-10"
              @click="showCloseConfirmModal = false"
            >
              Keep booking
            </Button>

            <Button
              variant="primary"
              class="px-5 h-10"
              @click="confirmCloseWidget"
            >
              Close window
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>      
  
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount, reactive, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import TicketWidget from './TicketWidget.vue'
import { useTicketingWidgetApi } from '@/composables/api/ticketing/useTicketingWidgetApi'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import LostTicketsWidget from './LostTicketsWidget.vue'

import { Dialog } from '@/components/Base/Headless'
import Button from '@/components/Base/Button'

import { useHelpers } from "@/composables/Helpers";
const { disableScroll, enableScroll } = useHelpers()

dayjs.extend(utc)
dayjs.extend(timezone)

/* =========================
   Types (only what we use)
   ========================= */
type Address = {
  street_1?: string|null   // Venue name (e.g. "Peanut Farm Reserve")
  street_2?: string|null   // Street address (e.g. "21 Spenser Street")
  locality?: string|null
  state?: string|null
  postal_code?: string|null
  country?: string|null
  formatted_address?: string|null
  formatted?: string|null  // API sometimes returns 'formatted' instead of 'formatted_address'
  google_maps_link?: string|null
}
type EventDisplay = {
  date_range?: string|null
  schedule?: string|null
  bottom_time?: string|null
  ics_url?: string|null
  google_url?: string|null
}
type ImageItem = { url: string|null; alt?: string|null }
type FaqItem = { id?: number; question: string; answer: string; sort_order?: number|null; is_published?: boolean }
type EventMap = { embed_url?: string|null }   // 🔹 NEW
type EventMedia = { url: string | null; filename?: string | null; mime?: string | null; original?: string | null }

type GalleryImage = {
  id: number
  url: string
  sort_order: number
  ticket_group_id?: number | null
}

type EventPayload = {
  id: string | number
  slug: string
  name: string
  landing_page_heading: string
  timezone?: string | null
  starts_at?: string | null
  ends_at?: string | null
  currency?: string | null
  images?: ImageItem[]
  email_header_image?: EventMedia | null
  landing_page_image?: EventMedia | null        // ✅ add
  landing_page_video?: EventMedia | null        // ✅ add
  popup_image?: EventMedia | null               // ✅ add
  gallery_images?: GalleryImage[]              // All gallery images (event-level + group-specific)
  address?: Address | null
  display?: EventDisplay | null
  description?: string | null
  conditions_of_entry?: string | null
  faqs?: FaqItem[]
  map?: EventMap | null
  groups?: TicketGroup[]            
}

type TicketGroup = {
  id: number
  label: string
  additional_event_information?: string | null
}

const route = useRoute()
const slug = (route.params.slug as string) || 'event'
const { fetchEventBySlug } = useTicketingWidgetApi()

const event = ref<EventPayload | null>(null)
const openWidget = ref(false)
const activeWidget = ref<'tickets' | 'lost'>('tickets')
const openFaq = reactive<Record<number, boolean>>({})

const showCloseConfirmModal = ref(false)

const isReady = computed(() => !!event.value)
const modalScrollRef = ref<HTMLElement | null>(null)
const backdropRef = ref<HTMLElement | null>(null)

const confirmCloseWidget = () => {
  showCloseConfirmModal.value = false
  openWidget.value = false
}

const modalClasses = computed(() => {
  if (activeWidget.value === 'tickets') {
    // Big, tall modal for the full ticket widget
    return 'close-button relative bg-white lg:rounded-2xl shadow-2xl w-full max-w-5xl lg:h-[93vh] overflow-hidden ticket-popup-modal'
  }

  // Much smaller modal for Lost Tickets
  return 'close-button relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden'
})

onMounted(async () => {
  const res = await fetchEventBySlug(slug)
  event.value = (res?.data && (res.data as any).id) ? res.data : res

  // ✅ Once event is loaded, update SEO & schema
  updateSeoFromEvent()
  injectEventSchema()
  
  // Check for voucher in URL and auto-open widget if present
  const urlParams = new URLSearchParams(window.location.search)
  const voucherFromUrl = urlParams.get('voucher') || urlParams.get('promo') || urlParams.get('code')
  const shouldAutoOpen = urlParams.get('open') === 'true' || urlParams.get('popup') === 'true'
  
  if (voucherFromUrl || shouldAutoOpen) {
    // Auto-open the ticket widget - the widget itself will auto-apply the voucher if present
    openTicketsWidget()
  }
})

onMounted(() => {
  // clear any prior widget session (if you want fresh start on each page load)
  Object.keys(localStorage).forEach(k => {
    if (k.startsWith('sj:session:')) localStorage.removeItem(k)
  })
})

// Listen for postMessage from parent window (for iframe embedding)
function handleParentMessage(messageEvent: MessageEvent) {
  const data = messageEvent.data
  if (!data || typeof data !== 'object') return
  
  // Handle open widget with optional voucher
  if (data.type === 'openWidget') {
    // If a code is provided, update URL so the widget can read it
    if (data.code) {
      const url = new URL(window.location.href)
      url.searchParams.set('voucher', data.code)
      window.history.replaceState({}, '', url.toString())
    }
    openTicketsWidget()
  }
  
  // Handle just applying a voucher (open widget + apply code)
  if (data.type === 'applyVoucher' && data.code) {
    const url = new URL(window.location.href)
    url.searchParams.set('voucher', data.code)
    window.history.replaceState({}, '', url.toString())
    openTicketsWidget()
  }
}

onMounted(() => {
  window.addEventListener('message', handleParentMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleParentMessage)
})

watch(openWidget, async (open) => {
  if (open) {
    // Stop slideshow when widget opens
    stopSlideshow()
    
    await nextTick();

    const scroller = modalScrollRef.value!;
    // Let the browser handle scrolling here; don't let it bubble to backdrop
    const stopBubble = (e: Event) => e.stopPropagation();
    scroller.addEventListener('touchmove', stopBubble, { passive: true });
    scroller.addEventListener('wheel', stopBubble, { passive: true });

    // now lock the page, but allow the scroller + scope blockers to backdrop
    disableScroll({
      allow: scroller,
      backdrop: backdropRef.value!,
    });

  } else {
    // Restart slideshow when widget closes
    startSlideshow()
    
    enableScroll();

    // Optional: clean up (if your modal mounts/unmounts each time you can skip)
    const scroller = modalScrollRef.value;
    if (scroller) {
      scroller.replaceWith(scroller); // cheap event listener reset
      // or removeEventListener if you kept a reference
    }
  }
});

const faqBeforeEnter = (el: Element) => {
  const elHtml = el as HTMLElement
  elHtml.style.maxHeight = '0px'
  elHtml.style.opacity = '0'
}

const faqEnter = (el: Element) => {
  const elHtml = el as HTMLElement
  const targetHeight = elHtml.scrollHeight

  // next frame so the browser sees the starting 0px first
  requestAnimationFrame(() => {
    elHtml.style.transition = 'max-height 0.22s ease, opacity 0.22s ease'
    elHtml.style.maxHeight = `${targetHeight}px`
    elHtml.style.opacity = '1'
  })
}

const faqBeforeLeave = (el: Element) => {
  const elHtml = el as HTMLElement
  // lock current height so we can animate from it
  elHtml.style.maxHeight = `${elHtml.scrollHeight}px`
  elHtml.style.opacity = '1'
}

const faqLeave = (el: Element) => {
  const elHtml = el as HTMLElement

  requestAnimationFrame(() => {
    elHtml.style.transition = 'max-height 0.22s ease, opacity 0.22s ease'
    elHtml.style.maxHeight = '0px'
    elHtml.style.opacity = '0'
  })
}

const closeWidget = () => {
  // Only show confirmation for the tickets widget, not for "Lost Tickets"
  if (activeWidget.value === 'tickets') {
    const widgetContainer = document.querySelector('.ticket-widget')
    const isOnSuccessScreen =
      widgetContainer?.querySelector('[data-step="success"]') !== null ||
      // Fallback: check for order number/ID in the success message
      widgetContainer?.textContent?.includes('Thanks for your order!') === true

    // If not on success, open our nice confirm modal instead of window.confirm
    if (!isOnSuccessScreen) {
      showCloseConfirmModal.value = true
      return
    }
  }

  // If we're on success (or it's the Lost Tickets widget), just close immediately
  openWidget.value = false
}

const openTicketsWidget = () => {
  activeWidget.value = 'tickets'
  openWidget.value = true
}

const openLostWidget = () => {
  activeWidget.value = 'lost'
  openWidget.value = true
}

/* ================
   Hero image (no flash)
   ================ */

/* ====================== Hero video + poster ====================== */
const videoUrl = computed(() => event.value?.landing_page_video?.url || null)

// Hero background / poster (what you see at the top of the page)
const heroPosterUrl = computed(
  () =>
    event.value?.landing_page_image?.url ||
    event.value?.email_header_image?.url ||
    event.value?.images?.[0]?.url ||
    null
)

// Open Graph / link preview image (what iMessage etc should use)
// Use first gallery image if available, otherwise fallback to popup_image or other images
const ogImageUrl = computed(() => {
  // First priority: first gallery image (for SEO)
  const firstGalleryImage = event.value?.gallery_images?.[0]?.url
  if (firstGalleryImage) {
    return firstGalleryImage
  }
  
  // Fallback to popup_image or other images
  return event.value?.popup_image?.url || heroPosterUrl.value || null
})

/* ====================== Hero Slider ====================== */
type HeroSlide = {
  url: string
  alt?: string
}

const heroSliderRef = ref<HTMLElement | null>(null)
const currentSlide = ref(0)
let slideInterval: ReturnType<typeof setInterval> | null = null

// Touch/swipe support
let touchStartX = 0
let touchEndX = 0
const minSwipeDistance = 50 // Minimum distance for a swipe

const handleTouchStart = (e: TouchEvent) => {
  touchStartX = e.touches[0].clientX
}

const handleTouchMove = (e: TouchEvent) => {
  touchEndX = e.touches[0].clientX
}

const handleTouchEnd = () => {
  if (!touchStartX || !touchEndX) return
  
  const distance = touchStartX - touchEndX
  
  if (Math.abs(distance) > minSwipeDistance) {
    if (distance > 0) {
      // Swipe left - next slide
      nextSlide()
    } else {
      // Swipe right - previous slide
      prevSlide()
    }
  }
  
  // Reset
  touchStartX = 0
  touchEndX = 0
}

// Build slides array from gallery_images (all images sorted by sort_order)
const heroSlides = computed<HeroSlide[]>(() => {
  const galleryImages = event.value?.gallery_images || []
  
  if (galleryImages.length > 0) {
    // Sort by sort_order and map to slides
    return galleryImages
      .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((img: any) => ({
        url: img.url,
        alt: `${event.value?.name || 'Event'} - Gallery Image ${img.id}`
      }))
  }
  
  // Fallback to popup image if no gallery images
  const imageUrl = event.value?.popup_image?.url || heroPosterUrl.value
  if (imageUrl) {
    return [{
      url: imageUrl,
      alt: `${event.value?.name || 'Event'} - Event Image`
    }]
  }
  
  return []
})

const nextSlide = () => {
  if (heroSlides.value.length <= 1) return
  currentSlide.value = (currentSlide.value + 1) % heroSlides.value.length
  // Reset auto-slide timer when manually navigating
  if (!openWidget.value) {
    startSlideshow()
  }
}

const prevSlide = () => {
  if (heroSlides.value.length <= 1) return
  currentSlide.value = currentSlide.value === 0 
    ? heroSlides.value.length - 1 
    : currentSlide.value - 1
  // Reset auto-slide timer when manually navigating
  if (!openWidget.value) {
    startSlideshow()
  }
}

const goToSlide = (index: number) => {
  currentSlide.value = index
  // Reset auto-slide timer when manually navigating
  if (!openWidget.value) {
    startSlideshow()
  }
}

const startSlideshow = () => {
  if (heroSlides.value.length <= 1) return
  // Clear any existing interval first
  stopSlideshow()
  slideInterval = setInterval(() => {
    nextSlide()
  }, 3500) // Change slide every 3 seconds
}

const stopSlideshow = () => {
  if (slideInterval) {
    clearInterval(slideInterval)
    slideInterval = null
  }
}

// Watch heroSlides to restart slideshow when images load
watch(heroSlides, (newSlides) => {
  if (newSlides.length > 1 && !openWidget.value) {
    // Only start if widget is not open
    startSlideshow()
  }
}, { immediate: false })

// Start slideshow when component mounts (if widget is not open)
onMounted(() => {
  if (!openWidget.value) {
    startSlideshow()
  }
})

onBeforeUnmount(() => {
  stopSlideshow()
})


const title = computed(() => event.value?.name || 'Event')


// =========================
// SEO title & description
// =========================

// Use router meta as primary source (optimized SEO copy), fallback to event data
const routeMetaTitle = route.meta.title as string | undefined
const routeMetaDescription = route.meta.description as string | undefined

const seoTitle = computed(() => {
  // 1. Prefer router meta title (hand-crafted SEO copy with keywords like "2026", "On Sale Now")
  if (routeMetaTitle) {
    return routeMetaTitle
  }
  // 2. Fallback to event name
  if (event.value?.name) {
    return `${event.value.name} Tickets | Summer Jam`
  }
  return 'Summer Jam Tickets'
})

const seoDescription = computed(() => {
  // 1. Prefer router meta description (hand-crafted SEO copy)
  if (routeMetaDescription) {
    return routeMetaDescription
  }
  // 2. Fallback to event description (stripped HTML, max 155 chars)
  const raw = event.value?.description
  if (raw && typeof document !== 'undefined') {
    const tmp = document.createElement('div')
    tmp.innerHTML = raw
    const text = (tmp.textContent || tmp.innerText || '').trim()
    if (text.length <= 155) return text
    return text.slice(0, 152) + '...'
  }
  return 'Buy tickets for Summer Jam basketball tournaments in Australia.'
})

// =========================
// Helpers for <head> tags
// =========================

function ensureLinkRel(rel: string, href: string) {
  if (typeof document === 'undefined') return

  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
}

// --- helpers to ensure meta tags exist / get updated ---
function ensureMetaName(name: string, content: string) {
  if (typeof document === 'undefined') return

  let tag = document.querySelector(
    `meta[name="${name}"]`
  ) as HTMLMetaElement | null

  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

function ensureMetaProperty(property: string, content: string) {
  if (typeof document === 'undefined') return

  let tag = document.querySelector(
    `meta[property="${property}"]`
  ) as HTMLMetaElement | null

  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

const groupsWithInfo = computed<TicketGroup[]>(() => {
  const groups = event.value?.groups || []
  return groups.filter(g => {
    const txt = g.additional_event_information
    return !!txt && txt.trim() !== ''
  })
})

// =========================
// Apply SEO from event
// =========================

function updateSeoFromEvent() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const titleVal = seoTitle.value
  const descVal = seoDescription.value
  const url = window.location.href
  const image = ogImageUrl.value || null 

  // Standard title + description
  document.title = titleVal
  ensureMetaName('description', descVal)

  // Canonical URL
  ensureLinkRel('canonical', url)

  // Open Graph tags
  ensureMetaProperty('og:title', titleVal)
  ensureMetaProperty('og:description', descVal)
  ensureMetaProperty('og:type', 'event')
  ensureMetaProperty('og:url', url)
  if (image) {
    ensureMetaProperty('og:image', image)
  }

  // Twitter Card tags (for better Twitter/X link previews)
  ensureMetaName('twitter:card', 'summary_large_image')
  ensureMetaName('twitter:title', titleVal)
  ensureMetaName('twitter:description', descVal)
  if (image) {
    ensureMetaName('twitter:image', image)
  }
}

// =========================
// JSON-LD Event schema
// =========================

function injectEventSchema() {
  if (!event.value) return
  if (typeof document === 'undefined') return

  const addr = event.value?.address
  
  // Extract street address: prefer street_2, fallback to first part of formatted address
  // e.g. "49 Spenser St, St Kilda VIC 3182, Australia" -> "49 Spenser St"
  const formattedAddr = addr?.formatted || addr?.formatted_address || ''
  const streetFromFormatted = formattedAddr.split(',')[0]?.trim() || undefined
  const streetAddress = addr?.street_2 || streetFromFormatted
  
  // Build structured PostalAddress for better SEO
  // street_1 = venue name, street_2 = street address
  const postalAddress = addr ? {
    "@type": "PostalAddress",
    streetAddress: streetAddress,
    addressLocality: addr.locality || undefined,
    addressRegion: addr.state || undefined,
    postalCode: addr.postal_code || undefined,
    addressCountry: addr.country || 'AU'
  } : undefined

  const data = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.value.name,
    startDate: start.value ? start.value.toISOString() : undefined,
    endDate: end.value ? end.value.toISOString() : undefined,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: addr?.street_1 || event.value.name,  // Venue name
      address: postalAddress || locationText.value
    },
    image: ogImageUrl.value ? [ogImageUrl.value] : undefined,
    description: seoDescription.value,
    organizer: {
      "@type": "Organization",
      name: "Summer Jam Australia",
      url: "https://www.summerjam.com.au"
    }
  }

  let script = document.querySelector('#event-schema') as HTMLScriptElement | null
  if (!script) {
    script = document.createElement('script')
    script.id = 'event-schema'
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(data, null, 2)
}



/* ======================
   Dates / Schedule (TZ)
   ====================== */
const tz = computed(() => event.value?.timezone || dayjs.tz.guess())
const start = computed(() =>
  event.value?.starts_at ? dayjs.utc(event.value.starts_at).tz(tz.value) : null
)
const end = computed(() =>
  event.value?.ends_at ? dayjs.utc(event.value.ends_at).tz(tz.value) : null
)

/** We trust backend to decide whether to show bottom_time */
const timeOnly = computed(() => event.value?.display?.bottom_time || '')

/* ======================
   Address / Map links
   ====================== */
const locationLines = computed(() => {
  const a = event.value?.address
  if (!a) return []
  // Line 1: Venue name (street_1) e.g. "Peanut Farm Reserve"
  // Line 2: City, State, Postcode (skip street_2 for cleaner UI)
  const line1 = a.street_1 || null
  const line2 = [a.locality, a.state, a.postal_code].filter(Boolean).join(', ') || null
  return [line1, line2].filter(Boolean)
})

const locationText = computed(() => {
  const a = event.value?.address
  if (!a) return ''
  // For UI: show venue name + city/state only (skip street address for cleaner display)
  // street_1 = venue name (e.g. "Peanut Farm Reserve")
  // street_2 = street address - NOT shown in UI, only in JSON-LD for SEO
  const parts = [a.street_1, a.locality, a.state, a.postal_code].filter(Boolean)
  if (parts.length > 0) return parts.join(', ')
  // Fallback to formatted address
  if (a.formatted || a.formatted_address) return a.formatted || a.formatted_address
  return ''
})

const mapQuery = computed(() => (locationText.value || event.value?.name || '').trim())

// 🔹 Prefer the API-provided embed URL; fall back to a search embed if missing
const embedMapUrl = computed(() => {
  const apiEmbed = event.value?.map?.embed_url?.trim()
  if (apiEmbed) return apiEmbed
  return `https://www.google.com/maps/embed/v1/search?key=YOUR_EMBED_API_KEY&q=${encodeURIComponent(mapQuery.value)}`
})

const mapUrl = computed(() => {
  const apiLink = event.value?.address?.google_maps_link
  return apiLink || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery.value)}`
})

/* ===========================
   Calendar links (from API)
   =========================== */
const icsUrl = computed(() => event.value?.display?.ics_url || '')

const googleUrl = computed(() => {
  if (event.value?.display?.google_url) return event.value.display.google_url
  const base = 'https://calendar.google.com/calendar/render?action=TEMPLATE'
  const dt = (d?: dayjs.Dayjs | null) => (d ? d.utc().format('YYYYMMDD[T]HHmmss[Z]') : '')
  const dates = (start.value && end.value) ? `${dt(start.value)}/${dt(end.value)}` : ''
  const params = new URLSearchParams({
    text: title.value,
    details: '',
    location: locationText.value || '',
    dates
  })
  return `${base}&${params}`
})

const outlookUrl = computed(() => {
  const base = 'https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose'
  const params = new URLSearchParams({
    subject: title.value,
    body: '',
    location: locationText.value || '',
    startdt: start.value ? start.value.toDate().toISOString() : '',
    enddt: end.value ? end.value.toDate().toISOString() : ''
  })
  return `${base}&${params}`
})

/* ======================
   FAQs
   ====================== */
const faqs = computed<FaqItem[]>(() => {
  const list = (event.value?.faqs || []).filter(f => f && (f.is_published ?? true))
  return list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0))
})
function toggleFaq(key: number) { openFaq[key] = !openFaq[key] }

// wherever you open/close the widget modal:
// Listen for close event from widget
onMounted(() => {
  window.addEventListener('sj:close-widget', () => {
    openWidget.value = false

    // optional: also clear sessions on close
    Object.keys(localStorage).forEach(k => {
      if (k.startsWith('sj:session:')) localStorage.removeItem(k)
    })
  })
})

</script>

<style>

.ticket-popup-modal {
  max-height:825px;
}
</style>
<style lang="scss" scoped>


@media(max-width:767px ){
  body {
    font-size:14px !important;
  }
}
.ticket-landing {
  a { transition: .3s; }

  a:hover {
    color:var(--color-widget-primary)
  }
}
.ticket-landing__top-bar {
  background-color: var(--color-widget-secondary);
  height: 64px;
  display: flex; justify-content: center; align-items: center;
  img { width: auto; height: 70%; }
}
.ticket-landing__footer {
  position:relative;
  /* the black strip below the footer */

  background-color: var(--color-widget-secondary);
  height: 96px;
  @media(max-width:767px) {
    margin-bottom:75px;
    &::after {
      content: "";              /* ← required */
      position: absolute;
      left: 0;                  /* anchor so it spans full width */
      right: 0;
      bottom: -50px;            /* sits below the footer */
      height: 50px;
      background-color: #000;
      display: block;           /* optional, but fine */
      pointer-events: none;     /* optional */
      z-index: 0;               /* adjust if you need it above/below neighbors */
    }    
  }
  a:hover { color: var(--color-widget-primary); }
  &__logo { height: 100%; width: auto; }
  &__socials {
    svg { height: 20px; width: auto;
      path { transition: .3s; } }
    a:hover svg path { fill: var(--color-widget-primary); }
  }
}

.ticket-landing__header-icon {
    height: 38px;
    width: auto;
    position: relative;
    top: 5px;  
    max-width:none;
}


.ticket-landing__mobile-cta--lost-tickets {
    width: 104px;
    /* float: right; */
    position: absolute;
    right: 15px;
    top: -23px;
    height: 23px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: black;
    color: white;
    padding: 0px !important;
    border-radius: 0px;
}

.ticket-landing__mobile--lost-tickets--btn {
    color: white;
    text-decoration: none;
    font-weight: bold;
    position: relative;
    bottom: 1px;
    font-size: 9px;
  &:hover {
    color: white !important;
  }
}

.ticket-landing__mobile--lost-tickets--btn:hover {
  color: white !important;
  text-decoration:underline;
}

.ticket-landing__mobile-cta--btn {
  align-items: center;
  padding-left: 15px;
  padding-right: 15px;
  padding-top: 15px;
  padding-bottom: 15px;  
  border-top: 1px solid #dedede;
  background-color: #ffffff;
}

.btn-ticketing-red {
  background-color:var(--color-widget-primary);
  color:white;
  transition:0.3s;
  border-radius:3px;
  &:hover {
    background-color:var(--color-widget-secondary);
  }
  padding-top:12px;
  padding-bottom:12px;
  width:100%;
}

/* ===== WYSIWYG content styles ===== */
.wysiwyg {
  color: #334155;              /* slate-700 */
  line-height: 1.5;
  font-size: 1rem;             /* 16px base */

}

.conditions_of_entry  {
  font-size:13px !important;
}

.wysiwyg :where(h1, h2, h3) {
  color: #0f172a;              /* slate-900 */
  font-weight: 700;
  line-height: 1.25;
  margin-top: 1.25rem;
  margin-bottom: .5rem;
}

.wysiwyg h1 { font-size: clamp(1.5rem, 1.2rem + 1vw, 2rem); }       /* 24–32 */
.wysiwyg h2 { font-size: clamp(1.25rem, 1.05rem + .7vw, 1.5rem); }   /* 20–24 */
.wysiwyg h3 { font-size: clamp(1.125rem, 1rem + .4vw, 1.25rem); }    /* 18–20 */

.wysiwyg :where(p) {
  margin: .75rem 0;
  @media(max-width:767px) {
    font-size:14px; 
  }
}

.wysiwyg :where(ul) {
  list-style: disc;
  padding-left: 1.25rem;
  margin: .5rem 0 .75rem;
  margin-left:20px;
  @media(max-width:767px) {
    margin-left:16px;
    font-size:14px;
  }  
}

.wysiwyg :where(li) {
  margin: .25rem 0;
}

// .wysiwyg :where(a) {
//   color: #fa552d;
//   text-decoration: underline;
//   text-underline-offset: 2px;
// }
.wysiwyg :where(strong) { font-weight: 700; color: #0f172a; }
.wysiwyg :where(em) { font-style: italic; }

/* ===== FAQ accordion polish ===== */
.faq-item { background: #fff; }
.faq-btn { padding-top: 16px; padding-bottom: 16px; }

.faq-panel {
  padding-top: 0;
  overflow: hidden; // still good to keep this
}

/* ===== Hero Wrapper (full bleed on all screens) ===== */
.hero-wrapper {
  background: #000;
  width: 100%;
}

/* ===== Top Bar ===== */
.hero-top-bar {
  background: #000;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 640px) {
    height: 56px;
  }
}

.hero-top-bar-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== Hero Slider ===== */
.hero-slider-section {
  background: #ffffff;
}

.hero-slider {
  position: relative;
  width: 100%;
  height: 240px; /* Reduced from 340px for mobile */
  overflow: hidden;
  touch-action: pan-y pinch-zoom; /* Enable touch gestures */
  
  @media (min-width: 640px) {
    height: 300px;
  }
  
  @media (min-width: 1024px) {
    aspect-ratio: 16 / 5;
    height:auto;
  }
}

.hero-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  background: #ffffff;
  
  &.active {
    opacity: 1;
  }
}

.hero-slide-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #ffffff;
}

.hero-slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #1e293b;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: #fff;
    transform: translateY(-50%) scale(1.05);
  }
  
  &--prev {
    left: 12px;
  }
  
  &--next {
    right: 12px;
  }
  
  @media (max-width: 640px) {
    width: 32px;
    height: 32px;
    
    &--prev { left: 8px; }
    &--next { right: 8px; }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
}

.hero-slider-dots {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 10;
}

.hero-slider-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.7);
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }
  
  &.active {
    background: #fff;
    border-color: #fff;
  }
}

.close-button {
  z-index:9999999;

  @media(max-width:1023px) {
    background: rgb(255 255 255 / 0.8);
    color: black;    
  }
}

.modal-scroll {
  overflow-y: scroll;       /* still scrollable */
  scrollbar-width: none;    /* Firefox */
  -ms-overflow-style: none; /* IE/Edge legacy */
}

.modal-scroll::-webkit-scrollbar {
  display: none;            /* Chrome, Safari, Edge */
}



</style>